import React, { useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  Divider,
  Chip,
  useMediaQuery,
  useTheme,
  alpha,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LoginIcon from '@mui/icons-material/Login';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ArticleIcon from '@mui/icons-material/Article';
import BarChartIcon from '@mui/icons-material/BarChart';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import ScienceIcon from '@mui/icons-material/Science';
import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboard';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SearchIcon from '@mui/icons-material/Search';
import BugReportIcon from '@mui/icons-material/BugReport';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import InventoryIcon from '@mui/icons-material/Inventory';
import HubIcon from '@mui/icons-material/Hub';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import AddLinkIcon from '@mui/icons-material/AddLink';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import GitHubIcon from '@mui/icons-material/GitHub';
import GavelIcon from '@mui/icons-material/Gavel';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import { cyber } from '../../theme';

interface NavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
  prefixMatch?: boolean;
}

interface ExternalItem {
  href: string;
  label: string;
  icon: React.ReactNode;
}

// Free pages, reachable without authentication.
const PUBLIC_ITEMS: NavItem[] = [
  { to: '/reports', label: 'Threat Reports', icon: <ArticleIcon fontSize="small" /> },
  { to: '/stats', label: 'Statistics', icon: <BarChartIcon fontSize="small" /> },
  { to: '/analytics', label: 'Agentic Reports', icon: <AnalyticsIcon fontSize="small" />, prefixMatch: true },
  { to: '/research', label: 'Research', icon: <ScienceIcon fontSize="small" /> },
];

// Pages that require sign-in. Navigating here while logged out shows the
// sign-in screen and, after login, redirects to the requested page.
const GATED_ITEMS: NavItem[] = [
  { to: '/', label: 'Full Dashboard', icon: <SpaceDashboardIcon fontSize="small" /> },
  { to: '/briefing', label: 'AI Briefing Agent', icon: <SmartToyIcon fontSize="small" /> },
  { to: '/ioc-search', label: 'IOC Search', icon: <SearchIcon fontSize="small" /> },
  { to: '/cve-search', label: 'CVE Search', icon: <BugReportIcon fontSize="small" /> },
  { to: '/entities', label: 'Threat Entities', icon: <TravelExploreIcon fontSize="small" /> },
  { to: '/stix-bundles', label: 'STIX Bundles', icon: <InventoryIcon fontSize="small" /> },
  { to: '/knowledge-graph', label: 'STIX Constellation', icon: <HubIcon fontSize="small" /> },
  { to: '/mcp-integration', label: 'MCP Server', icon: <IntegrationInstructionsIcon fontSize="small" /> },
  { to: '/submit', label: 'Submit Article', icon: <AddLinkIcon fontSize="small" /> },
];

const EXTERNAL_ITEMS: ExternalItem[] = [
  { href: 'https://docs.ti-mindmap-hub.com/', label: 'Documentation', icon: <MenuBookIcon fontSize="small" /> },
  { href: 'https://github.com/TI-Mindmap-HUB-Org/ti-mindmap-hub-research', label: 'GitHub', icon: <GitHubIcon fontSize="small" /> },
];

const LEGAL_ITEMS: NavItem[] = [
  { to: '/terms', label: 'Terms of Service', icon: <GavelIcon fontSize="small" /> },
  { to: '/privacy', label: 'Privacy Policy', icon: <PrivacyTipIcon fontSize="small" /> },
];

const PublicHeader: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const [platformAnchor, setPlatformAnchor] = useState<null | HTMLElement>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const isActive = (item: NavItem) =>
    item.prefixMatch ? location.pathname.startsWith(item.to) : location.pathname === item.to;

  const goSignIn = () => {
    setDrawerOpen(false);
    navigate('/');
  };

  const LockChip = (
    <Chip
      size="small"
      icon={<LockOutlinedIcon sx={{ fontSize: 12 }} />}
      label="Sign in"
      sx={{ ml: 'auto', height: 20, fontSize: '0.65rem', bgcolor: alpha(cyber.primary, 0.12), color: 'primary.main' }}
    />
  );

  const brand = (
    <Box
      component={RouterLink}
      to="/landingpage"
      sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none', mr: 2 }}
    >
      <Box
        component="img"
        src="/TI-Mindmap-HUB-logo.png"
        alt="TI Mindmap HUB Logo"
        sx={{ height: 30, width: 30, mr: 1, filter: 'drop-shadow(0 0 8px rgba(123, 76, 255, 0.5))' }}
      />
      <Typography variant="h6" noWrap sx={{ fontWeight: 800, letterSpacing: '-0.02em', ...cyber.gradientText }}>
        TI Mindmap HUB
      </Typography>
    </Box>
  );

  // --- Desktop ---
  const desktopNav = (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexGrow: 1 }}>
        {PUBLIC_ITEMS.map((item) => (
          <Button
            key={item.to}
            component={RouterLink}
            to={item.to}
            startIcon={item.icon}
            sx={{
              color: isActive(item) ? 'primary.main' : 'text.secondary',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { color: 'text.primary', bgcolor: alpha(cyber.primary, 0.08) },
            }}
          >
            {item.label}
          </Button>
        ))}

        <Button
          onClick={(e) => setPlatformAnchor(e.currentTarget)}
          endIcon={<ExpandMoreIcon />}
          startIcon={<LockOutlinedIcon fontSize="small" />}
          sx={{
            color: 'text.secondary',
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': { color: 'text.primary', bgcolor: alpha(cyber.primary, 0.08) },
          }}
        >
          Platform
        </Button>
        <Menu
          anchorEl={platformAnchor}
          open={Boolean(platformAnchor)}
          onClose={() => setPlatformAnchor(null)}
          slotProps={{ paper: { sx: { minWidth: 260 } } }}
        >
          <Typography variant="overline" sx={{ px: 2, color: 'text.secondary', fontSize: '0.65rem' }}>
            Requires sign-in
          </Typography>
          {GATED_ITEMS.map((item) => (
            <MenuItem
              key={item.to}
              component={RouterLink}
              to={item.to}
              onClick={() => setPlatformAnchor(null)}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
              {LockChip}
            </MenuItem>
          ))}
          <Divider />
          <Typography variant="overline" sx={{ px: 2, color: 'text.secondary', fontSize: '0.65rem' }}>
            Resources
          </Typography>
          {EXTERNAL_ITEMS.map((item) => (
            <MenuItem
              key={item.href}
              component="a"
              href={item.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setPlatformAnchor(null)}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
              <OpenInNewIcon sx={{ fontSize: 14, ml: 'auto', color: 'text.secondary' }} />
            </MenuItem>
          ))}
          <Divider />
          {LEGAL_ITEMS.map((item) => (
            <MenuItem
              key={item.to}
              component={RouterLink}
              to={item.to}
              onClick={() => setPlatformAnchor(null)}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
            </MenuItem>
          ))}
        </Menu>
      </Box>

      <Button
        variant="contained"
        startIcon={<LoginIcon />}
        onClick={goSignIn}
        sx={{
          background: cyber.gradient,
          textTransform: 'none',
          fontWeight: 700,
          '&:hover': { background: cyber.gradientHover },
        }}
      >
        Sign In
      </Button>
    </>
  );

  // --- Mobile drawer ---
  const drawer = (
    <Box sx={{ width: 280 }} role="presentation">
      <Box sx={{ p: 2 }}>{brand}</Box>
      <Divider />

      <Typography variant="overline" sx={{ pl: 2, pt: 1.5, display: 'block', color: 'text.secondary' }}>
        Free access
      </Typography>
      <List dense>
        {PUBLIC_ITEMS.map((item) => (
          <ListItem key={item.to} disablePadding>
            <ListItemButton component={RouterLink} to={item.to} selected={isActive(item)} onClick={() => setDrawerOpen(false)}>
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Divider />
      <Typography variant="overline" sx={{ pl: 2, pt: 1.5, display: 'block', color: 'text.secondary' }}>
        Requires sign-in
      </Typography>
      <List dense>
        {GATED_ITEMS.map((item) => (
          <ListItem key={item.to} disablePadding>
            <ListItemButton component={RouterLink} to={item.to} onClick={() => setDrawerOpen(false)}>
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
              <LockOutlinedIcon sx={{ fontSize: 16, color: 'primary.main' }} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Divider />
      <Typography variant="overline" sx={{ pl: 2, pt: 1.5, display: 'block', color: 'text.secondary' }}>
        Resources
      </Typography>
      <List dense>
        {EXTERNAL_ITEMS.map((item) => (
          <ListItem key={item.href} disablePadding>
            <ListItemButton component="a" href={item.href} target="_blank" rel="noopener noreferrer" onClick={() => setDrawerOpen(false)}>
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
              <OpenInNewIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            </ListItemButton>
          </ListItem>
        ))}
        {LEGAL_ITEMS.map((item) => (
          <ListItem key={item.to} disablePadding>
            <ListItemButton component={RouterLink} to={item.to} onClick={() => setDrawerOpen(false)}>
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Box sx={{ p: 2 }}>
        <Button
          fullWidth
          variant="contained"
          startIcon={<LoginIcon />}
          onClick={goSignIn}
          sx={{ background: cyber.gradient, textTransform: 'none', fontWeight: 700, '&:hover': { background: cyber.gradientHover } }}
        >
          Sign In
        </Button>
      </Box>
    </Box>
  );

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        bgcolor: alpha(cyber.bgSidebar, 0.85),
        backdropFilter: 'blur(12px)',
        borderBottom: `1px solid ${cyber.border}`,
      }}
    >
      <Toolbar>
        {isMobile ? (
          <>
            <IconButton edge="start" color="inherit" onClick={() => setDrawerOpen(true)} sx={{ mr: 1 }}>
              <MenuIcon />
            </IconButton>
            {brand}
            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant="contained"
              size="small"
              startIcon={<LoginIcon />}
              onClick={goSignIn}
              sx={{ background: cyber.gradient, textTransform: 'none', fontWeight: 700, '&:hover': { background: cyber.gradientHover } }}
            >
              Sign In
            </Button>
            <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
              {drawer}
            </Drawer>
          </>
        ) : (
          <>
            {brand}
            {desktopNav}
          </>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default PublicHeader;
